<template>
  <Transition name="action-indicator">
    <div v-if="visible" class="action-indicator" :class="{ 'action-indicator--with-text': currentText }" aria-live="polite" :aria-label="ariaLabel">
      <div class="action-indicator-icon" v-html="currentIcon"></div>
      <div v-if="currentText" class="action-indicator-text">{{ currentText }}</div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import {
  playIcon, pauseIcon, forward10Icon, backward10Icon,
  volumeHighIcon, volumeLowIcon, volumeOffIcon, speedIcon
} from '../../assets/icons'

const visible = ref(false)
const currentIcon = ref('')
const currentText = ref('')
const ariaLabel = ref('')

let hideTimer: ReturnType<typeof setTimeout> | null = null

const iconMap: Record<string, string> = {
  play: playIcon,
  pause: pauseIcon,
  forward: forward10Icon,
  backward: backward10Icon,
  volumeUp: volumeHighIcon,
  volumeDown: volumeLowIcon,
  mute: volumeOffIcon,
  speed: speedIcon,
}

function show(type: string, label?: string, text?: string): void {
  currentIcon.value = iconMap[type] || ''
  currentText.value = text || ''
  ariaLabel.value = label || type
  visible.value = true

  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = setTimeout(() => {
    visible.value = false
  }, 600)
}

defineExpose({ show })
</script>

<style scoped lang="scss">
.action-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 50;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 1rem;
  backdrop-filter: blur(8px);
}

.action-indicator-icon {
  width: 50px;
  height: 50px;
  color: white;
  opacity: 0.9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-indicator--with-text .action-indicator-icon {
  width: 34px;
  height: 34px;
}

.action-indicator-icon :deep(svg) {
  width: 100%;
  height: 100%;
  fill: currentColor;
  stroke: currentColor;
}

.action-indicator-text {
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-top: 6px;
  line-height: 1;
  white-space: nowrap;
}

.action-indicator-enter-active {
  animation: indicator-in 150ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.action-indicator-leave-active {
  animation: indicator-out 150ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

@keyframes indicator-in {
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}

@keyframes indicator-out {
  from { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  to { opacity: 0; transform: translate(-50%, -50%) scale(1.05); }
}

@media (prefers-reduced-motion: reduce) {
  .action-indicator-enter-active,
  .action-indicator-leave-active {
    animation-duration: 1ms;
  }
}

@media (max-width: 768px) {
  .action-indicator { width: 80px; height: 80px; }
  .action-indicator-icon { width: 42px; height: 42px; }
}
</style>
